'use client'

import { useRowLabel } from '@payloadcms/ui'
import React from 'react'

/**
 * Etichetta di riga per le `sources` di un Post, che ora nascono chiuse (initCollapsed).
 *
 * Senza questa, una fonte collassata si presenterebbe come "Sources 03" e per ritrovarne una
 * bisognerebbe riaprirle tutte — cioè esattamente ciò che il collasso doveva togliere di mezzo.
 * Nella riga chiusa si legge quindi da CHI viene il materiale (host), che cosa dice (titolo) e
 * quanto pesa nell'articolo (fatti del ledger che arrivano da lì).
 *
 * IL NUMERO DI RIGA HA UN SECONDO MESTIERE da quando il corpo cita le fonti in apice: «03» è la
 * fonte a cui rimanda il piccolo 3 che si vede in pagina (src/lib/citations.ts). Chi rilegge un
 * articolo qui dentro può quindi partire dall'apice e trovare la riga, che è il motivo per cui la
 * numerazione resta la prima cosa dell'etichetta.
 */

type Source = {
  factCount?: null | number
  host?: null | string
  title?: null | string
  trustClass?: null | string
  url?: null | string
}

const clamp = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text

/** Se `host` non è stato salvato (fonti vecchie), lo si ricava dall'url. */
const hostOf = (source: Source): string => {
  if (source.host) return source.host
  if (!source.url) return ''
  try {
    return new URL(source.url).hostname
  } catch {
    return source.url
  }
}

export default function SourceRowLabel() {
  const { data, rowNumber } = useRowLabel<Source>()
  const source = data ?? {}

  const parts = [String((rowNumber ?? 0) + 1).padStart(2, '0')]
  const host = hostOf(source)
  if (host) parts.push(host)
  if (source.title) parts.push(clamp(source.title, 70))
  const facts = Number(source.factCount ?? 0)
  if (facts) parts.push(`${facts} ${facts === 1 ? 'fatto' : 'fatti'}`)

  return (
    <span style={{ pointerEvents: 'none' }}>
      {parts.join(' · ')}
      {source.trustClass ? (
        <span style={{ color: 'var(--theme-elevation-500)' }}> ({source.trustClass})</span>
      ) : null}
    </span>
  )
}
